import type { FindOrganizationCaseRecordsRequest } from '@compass/client/types/case_record';
import { Sort } from '@compass/client/types/common';
import type { Workflow } from '@compass/client/types/workflow';
import { EmptyState } from '@components/EmptyState';
import { GenericTable } from '@components/GenericTable';
import { QuickFilters } from '@components/QuickFilters';
import { useDebounce } from '@hooks/useDebounce';
import { useStore } from '@store/index';
import { Factory, UserCircle, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { AssigneeDropdown, type AssigneeOption } from '@/components/AssigneeDropdown';
import { DownloadDropdown } from '@/components/DownloadDropdown';
import { ErrorContainer } from '@/components/ErrorContainer';
import { Breadcrumb, BreadcrumbPortal, HeaderActionsPortal } from '@/components/Header';
import { type CasesTableContext, createCasesTableConfig } from '@/components/TableBuilder';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SearchInput } from '@/components/ui/search-input';
import {
  SearchableMultiSelect,
  type SearchableMultiSelectOption,
} from '@/components/ui/searchable-multi-select';
import { knipperPqcLifecycleFiltersConfig } from '@/config/quickFilters.config';
import { WorkflowType } from '@/constants/dispositions';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  getFieldNameFromPath,
  getSearchFieldEnum,
  isAdvancedSearchField,
  useUpdateAssignmentForCase,
} from '@/hooks/useCases';
import { useDownloadOrgCaseRecords } from '@/hooks/useDownloadOrgCaseRecords';
import { useFilterParams } from '@/hooks/useFilterParams';
import { useOrgAssigneeOptions } from '@/hooks/useOrgAssigneeOptions';
import { useOrgCaseCounts } from '@/hooks/useOrgCaseCounts';
import { useOrgCases } from '@/hooks/useOrgCases';
import { useOrgPqcWorkflows } from '@/hooks/useOrgPqcWorkflows';
import { useSeesAllOrgCases } from '@/hooks/useSeesAllOrgCases';
import { useUsersByIds } from '@/hooks/useUsers';
import type { ExportFormat, OrgExportColumnsMode } from '@/types/export.types';
import type { SortState } from '@/types/table';
import { partitionCaseIdSearch } from '@/utils/caseSearch';
import { dateToNaiveString, naiveDateToUtc, naiveStringToDate } from '@/utils/date';
import { buildRoutePath } from '@/utils/routes';

import { ExportCasesDialog } from './ExportCasesDialog';
import { getDefaultDateRange, getPqcPinnedDateRange } from './hooks/useCasesFilters';
import { ManufacturerPickerDialog } from './ManufacturerPickerDialog';

// PQC quick-filter tabs (Pending / Submitted / Completed / Cancelled / Duplicate / Other / All).
// Shared across all PQC workflows, so it's safe to use for the cross-program view.
const PQC_FILTERS = knipperPqcLifecycleFiltersConfig;

// Search fields offered on the org PQC page: exact Case-ID lookup plus a couple of
// Atlas free-text fields (the PQC input schema is uniform across manufacturers).
const SEARCH_OPTIONS = [
  { key: 'case_id', label: 'Case ID' },
  { key: 'input_data.data.patient_full_name', label: 'Patient Name' },
  { key: 'input_data.data.reporter_phone', label: 'Reporter Phone' },
];

// Non-empty sentinel for the "Unassigned" assignee-filter value. The backend keys
// unassigned cases on an empty string in `assigned_user_ids`, but the empty string is
// dropped by the URL serializer's `.filter(Boolean)` (so it wouldn't survive a refresh).
// We therefore carry this sentinel in filter state and translate it back to '' only when
// building the request. Reuses the value already used as the Unassigned option's `id`.
const UNASSIGNED = '__unassigned__';

// Translate assignee-filter values into the request's `assigned_user_ids` (the Unassigned
// sentinel becomes the empty string the backend expects). Returns undefined for no filter.
const toAssignedUserIds = (values: string[]): string[] | undefined =>
  values.length > 0 ? values.map(v => (v === UNASSIGNED ? '' : v)) : undefined;

/**
 * Tier-2 org-wide PQC cases page.
 *
 * Lists PQC cases across every program the user can access (via the org-wide
 * `FindOrganizationCaseRecords` endpoint, scoped server-side to accessible
 * programs) with the standard cases filters: status quick-filter tabs, assignee,
 * date range, Case-ID search, plus a "manufacturer" (PQC workflow) multi-select.
 * Filtering + cursor pagination are server-side (no count badges).
 */
export const Tier2CasesContainer: React.FC = () => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const currentUser = useStore(state => state.user);
  // Current user's assignee-filter value, keyed the same way options are (email preferred,
  // id fallback) so it matches an option and the default pre-selection checks it.
  const meValue = currentUser?.email || currentUser?.id || '';
  // Supervisors and org admins oversee every case in the org, so they land on the
  // full list (empty assignee filter) rather than the "my cases + unassigned"
  // default. Resolved before mount (CasesRouter gates this page on the roles
  // query), so the assignee-filter lazy initializer below reads the correct value.
  const seesAllCases = useSeesAllOrgCases();
  const getProgramsArray = useStore(state => state.getProgramsArray);
  const getTimeZone = useStore(state => state.getTimeZone);
  const timeZone = getTimeZone();
  const programs = getProgramsArray();
  // Page size is persisted in the store (shared with the per-program cases
  // page), so the user's choice survives refresh and navigation.
  const pageSize = useStore(state => state.casesPageSize);
  const setPageSize = useStore(state => state.setCasesPageSize);

  // Manufacturers (PQC workflows the user can access) — one org-scoped call.
  const { pqcWorkflows, isLoading: workflowsLoading, error: workflowsError } = useOrgPqcWorkflows();

  // "Create Case" manufacturer picker — the org-wide view has no selected
  // program/workflow, so the user picks the manufacturer (PQC workflow) first.
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Export column-mode dialog — non-null while open; carries the format (CSV/
  // Excel) picked in the download menu.
  const [exportDialogFormat, setExportDialogFormat] = useState<ExportFormat | null>(null);

  // Assignee options across all accessible programs (org-wide), not just the
  // current program — matches the cross-program case list. `allUserIds`/
  // `assigneeUsersMap` drive the assignee filter dropdown; `assigneeOptions` (a
  // stable wrapper of the same fields) is injected into the Assignee column's
  // inline picker. The hook's own return is a fresh object each render, so
  // re-wrap in a memo keyed on its (individually stable) fields to keep the
  // table config from recomputing on every render.
  const orgAssignee = useOrgAssigneeOptions();
  const {
    groups,
    allUserIds,
    usersMap: assigneeUsersMap,
    isLoading: assigneeLoading,
  } = orgAssignee;
  const assigneeOptions = useMemo(
    () => ({ groups, allUserIds, usersMap: assigneeUsersMap, isLoading: assigneeLoading }),
    [groups, allUserIds, assigneeUsersMap, assigneeLoading],
  );

  // Cross-program inline reassignment (each case carries its own org/program).
  const updateAssignmentForCase = useUpdateAssignmentForCase();

  // Org-wide export (CSV/XLSX) of the currently-filtered cases.
  const { downloadOrgCaseRecords, isExporting } = useDownloadOrgCaseRecords();

  // ─── URL ⇄ filter-state sync ──────────────────────────────────────────────
  // Filter state is mirrored to the URL query string (like the per-program
  // page) so filters survive refresh, are deep-linkable, and the back button
  // restores them. Initial state is seeded from the URL once on mount; the
  // write-through effect below keeps the URL in step. The keyset pagination
  // cursor is intentionally not deep-linked — page resets to 1 on load.
  const { searchParams, getInitialValues, syncFiltersToUrl } = useFilterParams();
  const [initialValues] = useState(() => getInitialValues());

  // Active search type = whichever search key the URL populated (validated
  // against the offered options); default to exact Case-ID lookup.
  const initialSearchType = useMemo(() => {
    const active = Object.keys(initialValues.searchValues).find(k => initialValues.searchValues[k]);
    return active && SEARCH_OPTIONS.some(o => o.key === active) ? active : 'case_id';
  }, [initialValues]);

  // Picker default (today). Tabs with `hidesDatePicker` (the work queues)
  // ignore it and are pinned to the last 12 months (AQ-863).
  const defaultRange = useMemo(() => getDefaultDateRange(WorkflowType.PQC, timeZone), [timeZone]);
  const pinnedRange = useMemo(() => getPqcPinnedDateRange(timeZone), [timeZone]);

  // Filters (seeded from the URL on mount).
  const [activeQuickFilter, setActiveQuickFilter] = useState<string>(() => {
    const raw = searchParams.get('filter');
    return raw && PQC_FILTERS.filters.some(t => t.id === raw)
      ? raw
      : (PQC_FILTERS.filters[0]?.id ?? 'all');
  });
  const [selectedWorkflowIds, setSelectedWorkflowIds] = useState<string[]>(
    initialValues.manufacturers ?? [],
  );
  // Assignee filter: respect an explicit URL selection; otherwise, on a fresh visit
  // (no `assigned` param), default based on role — supervisors and org admins see
  // all cases (empty filter), while everyone else opens on "my cases + up-for-grabs"
  // (current user + Unassigned) without any clicks.
  const [assignedFilter, setAssignedFilter] = useState<string[]>(() => {
    if (searchParams.get('assigned') !== null) {
      return initialValues.assigned ?? [];
    }
    if (seesAllCases) {
      return [];
    }
    return meValue ? [meValue, UNASSIGNED] : [UNASSIGNED];
  });
  const [dateFrom, setDateFrom] = useState<string>(initialValues.dateFrom || defaultRange.from);
  const [dateTo, setDateTo] = useState<string>(initialValues.dateTo || defaultRange.to);
  const [activeSearchType, setActiveSearchType] = useState<string>(initialSearchType);
  const [searchValue, setSearchValue] = useState<string>(
    initialValues.searchValues[initialSearchType] || '',
  );
  const debouncedSearch = useDebounce(searchValue, 500);
  // User-controllable column sort (created_at). Seeded from the URL, else the
  // active tab's defaultSort; resettable by clicking the column header
  // (standard tabs only).
  const [sortState, setSortState] = useState<SortState[]>(() => {
    if (initialValues.sortState.length > 0) return initialValues.sortState;
    const tab = PQC_FILTERS.filters.find(f => f.id === activeQuickFilter);
    return tab?.defaultSort ?? [];
  });

  const activeTab = useMemo(
    () => PQC_FILTERS.filters.find(f => f.id === activeQuickFilter),
    [activeQuickFilter],
  );
  const hidesDatePicker = !!activeTab?.hidesDatePicker;
  // Range the list (and the active tab's count) actually queries with.
  const listDateFrom = hidesDatePicker ? pinnedRange.from : dateFrom;
  const listDateTo = hidesDatePicker ? pinnedRange.to : dateTo;
  // Standard-path sort comes from the (user-overridable) sortState.
  const sortDirection = sortState.find(s => s.field === 'created_at')?.direction;

  // Quick-filter tab change: snap the sort back to that tab's default (Pending =
  // oldest-first, others = newest-first); the user can then re-sort via the
  // header. Done in the handler (not a mount effect) so a URL-restored sort
  // isn't clobbered on load.
  const handleQuickFilterChange = useCallback((filterId: string) => {
    setActiveQuickFilter(filterId);
    const tab = PQC_FILTERS.filters.find(f => f.id === filterId);
    setSortState(tab?.defaultSort ?? []);
  }, []);

  // Write the current filter state back to the URL whenever it changes (replace,
  // so it doesn't spam history). Pagination cursor is intentionally omitted.
  useEffect(() => {
    syncFiltersToUrl(
      activeQuickFilter,
      { [activeSearchType]: debouncedSearch },
      assignedFilter,
      dateFrom,
      dateTo,
      sortState,
      undefined,
      true,
      undefined,
      selectedWorkflowIds,
    );
  }, [
    syncFiltersToUrl,
    activeQuickFilter,
    activeSearchType,
    debouncedSearch,
    assignedFilter,
    dateFrom,
    dateTo,
    sortState,
    selectedWorkflowIds,
  ]);

  // Case-ID box: ObjectIds → case_record_ids, human-readable case codes →
  // case_codes (the org endpoint matches both). Mirrors the per-program page.
  const { caseRecordIds, caseCodes } = useMemo(
    () => (activeSearchType === 'case_id' ? partitionCaseIdSearch(debouncedSearch) : {}),
    [activeSearchType, debouncedSearch],
  );
  const isSearchingByCaseId = !!caseRecordIds || !!caseCodes;
  // Free-text Atlas search on an advanced input_data field (patient name, phone, …).
  const isFreeTextSearch =
    activeSearchType !== 'case_id' && isAdvancedSearchField(activeSearchType) && !!debouncedSearch;
  // Atlas/keyset path (output-condition tabs or free-text) sorts by a fixed sort_field;
  // disable column-header sort there, like the program page.
  const isAdvanced = isFreeTextSearch || !!activeTab?.filter.outputConditions?.length;

  // Server-side cursor pagination via a local page→cursor stack (index = page-1).
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const currentPage = cursorStack.length;
  const currentCursor = cursorStack[cursorStack.length - 1];

  // Reset to page 1 whenever a request-affecting filter changes.
  useEffect(() => {
    setCursorStack([undefined]);
  }, [
    activeQuickFilter,
    selectedWorkflowIds,
    assignedFilter,
    dateFrom,
    dateTo,
    debouncedSearch,
    activeSearchType,
    sortState,
    pageSize,
  ]);

  // Build the org-wide request. PQC-only scope: specific manufacturers →
  // workflow_ids; otherwise all PQC via workflow_types. Four mutually exclusive
  // modes: Case-ID exact lookup, free-text Atlas search, output-condition tab
  // (Atlas), and standard state tab (Mongo find).
  const request: Omit<FindOrganizationCaseRecordsRequest, 'organization_id'> = useMemo(() => {
    const scope =
      selectedWorkflowIds.length > 0
        ? { workflow_ids: selectedWorkflowIds }
        : { workflow_types: [WorkflowType.PQC] };
    const base = { ...scope, limit: pageSize, start_after_id: currentCursor };

    // Case-ID exact lookup — ignore status/assignee/date filters.
    if (isSearchingByCaseId) {
      return { ...base, case_record_ids: caseRecordIds, case_codes: caseCodes };
    }

    const common = {
      assigned_user_ids: toAssignedUserIds(assignedFilter),
      created_at_after: listDateFrom ? naiveDateToUtc(listDateFrom, timeZone, 'start') : undefined,
      created_at_before: listDateTo ? naiveDateToUtc(listDateTo, timeZone, 'end') : undefined,
    };

    // Free-text Atlas search (input_data index). Can't combine with output
    // conditions, so it supersedes the tab's output filter but keeps its states.
    if (isFreeTextSearch) {
      return {
        ...base,
        ...common,
        states: activeTab?.filter.states,
        field: getSearchFieldEnum(getFieldNameFromPath(activeSearchType)) ?? undefined,
        value: debouncedSearch,
      };
    }

    // Output-condition tab (Atlas advanced path): Follow-up / Submitted / Ready-to-Complete.
    if (activeTab?.filter.outputConditions?.length) {
      return {
        ...base,
        ...common,
        states: activeTab.filter.states,
        output_conditions: activeTab.filter.outputConditions,
        sort_field: activeTab.advancedSort,
        sort_order: activeTab.advancedSortAsc === false ? Sort.SORT_DESC : Sort.SORT_ASC,
      };
    }

    // Standard state tab (Mongo find path).
    return {
      ...base,
      ...common,
      states: activeTab?.filter.states,
      sort_by_created_at: sortDirection as FindOrganizationCaseRecordsRequest['sort_by_created_at'],
    };
  }, [
    selectedWorkflowIds,
    pageSize,
    currentCursor,
    isSearchingByCaseId,
    caseRecordIds,
    caseCodes,
    isFreeTextSearch,
    timeZone,
    activeSearchType,
    debouncedSearch,
    activeTab,
    assignedFilter,
    listDateFrom,
    listDateTo,
    sortDirection,
  ]);

  const {
    cases: rawCases,
    isLoading: casesLoading,
    error,
    hasMore,
    nextCursor,
    nextPageToken,
  } = useOrgCases(request);

  // Quick-filter tab badge counts (org-wide). Scope mirrors the list request's
  // PQC scope (selected manufacturers → workflow_ids, else all PQC) plus the
  // active filters, so badge totals track the visible list (matching the program
  // page). Case-ID search is an exact lookup that — like the list request — drops
  // the assignee/date filters; free-text search forwards the Atlas field/value.
  const countsScope = useMemo(() => {
    const scope =
      selectedWorkflowIds.length > 0
        ? { workflowIds: selectedWorkflowIds }
        : { workflowTypes: [WorkflowType.PQC] };

    if (isSearchingByCaseId) {
      return { ...scope, caseRecordIds, caseCodes };
    }

    return {
      ...scope,
      assignedUserIds: toAssignedUserIds(assignedFilter),
      createdAtAfter: dateFrom ? naiveDateToUtc(dateFrom, timeZone, 'start') : undefined,
      createdAtBefore: dateTo ? naiveDateToUtc(dateTo, timeZone, 'end') : undefined,
      ...(isFreeTextSearch
        ? {
            searchField: getSearchFieldEnum(getFieldNameFromPath(activeSearchType)) ?? undefined,
            searchValue: debouncedSearch,
          }
        : {}),
    };
  }, [
    selectedWorkflowIds,
    assignedFilter,
    dateFrom,
    dateTo,
    isSearchingByCaseId,
    caseRecordIds,
    caseCodes,
    isFreeTextSearch,
    timeZone,
    activeSearchType,
    debouncedSearch,
  ]);
  const pinnedDates = useMemo(
    () =>
      isSearchingByCaseId
        ? undefined
        : {
            createdAtAfter: naiveDateToUtc(pinnedRange.from, timeZone, 'start'),
            createdAtBefore: naiveDateToUtc(pinnedRange.to, timeZone, 'end'),
          },
    [isSearchingByCaseId, pinnedRange, timeZone],
  );
  const { counts: quickFilterCounts, isLoading: countsLoading } = useOrgCaseCounts(
    PQC_FILTERS.filters,
    countsScope,
    pinnedDates,
  );

  // Created-by users for the table.
  const createdByUserIds = useMemo(
    () => rawCases.map(c => c.created_by).filter((id): id is string => !!id),
    [rawCases],
  );
  const { usersMap, isLoading: usersLoading } = useUsersByIds(createdByUserIds);

  // Manufacturer name + SLA per row are resolved by workflow_id from the org
  // workflow list (one ListOrganizationWorkflows call) — covers every PQC
  // workflow the cases belong to, with no per-program fan-out.
  const workflowNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of pqcWorkflows) {
      if (w.id) map.set(w.id, w.name || w.id);
    }
    return map;
  }, [pqcWorkflows]);

  // One-line description of what an export covers, for the export dialog
  // (mirrors the list request's filters; deliberately no case counts). Search
  // values are never echoed — the Case-ID path just notes the lookup, since it
  // drops the other filters from the request.
  const exportFiltersSummary = useMemo(() => {
    const manufacturers =
      selectedWorkflowIds.length === 0
        ? 'All manufacturers'
        : selectedWorkflowIds.length === 1
          ? (workflowNameById.get(selectedWorkflowIds[0]) ?? '1 manufacturer')
          : `${selectedWorkflowIds.length} manufacturers`;
    if (isSearchingByCaseId) {
      return `${manufacturers} · Case ID lookup`;
    }
    const parts = [manufacturers, `Status: ${activeTab?.name ?? 'All'}`];
    if (listDateFrom) {
      parts.push(`${listDateFrom} – ${listDateTo}`);
    }
    return parts.join(' · ');
  }, [
    selectedWorkflowIds,
    workflowNameById,
    isSearchingByCaseId,
    activeTab,
    listDateFrom,
    listDateTo,
  ]);

  const slaByWorkflowId = useMemo(() => {
    const map = new Map<string, number>();
    for (const w of pqcWorkflows) {
      if (w.id && typeof w.sla?.minutes === 'number') map.set(w.id, w.sla.minutes);
    }
    return map;
  }, [pqcWorkflows]);

  const programsById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of programs) {
      if (p.id) map.set(p.id, p.name || p.id);
    }
    return map;
  }, [programs]);

  // Status badge colors/labels: PQC `ui.status` is uniform across manufacturers,
  // so use the first PQC workflow's config for the cross-program view.
  const statusConfig = useMemo(() => pqcWorkflows[0]?.ui?.status, [pqcWorkflows]);

  const tableContext: CasesTableContext = useMemo(
    () => ({
      programsById,
      slaByWorkflowId,
      workflowNameById,
      pqcView: true,
      statusConfig,
      assigneeOptions,
    }),
    [programsById, slaByWorkflowId, workflowNameById, statusConfig, assigneeOptions],
  );

  // Manufacturer options, grouped by program.
  const manufacturerOptions: SearchableMultiSelectOption[] = useMemo(
    () =>
      pqcWorkflows
        .filter(w => !!w.id)
        .map(w => ({
          value: w.id!,
          label: w.name || w.id!,
          group: (w.program_id ? programsById.get(w.program_id) : undefined) || undefined,
        })),
    [pqcWorkflows, programsById],
  );

  // Assignee filter options (with an "Unassigned" entry), mirroring CasesContainer.
  const assigneeFilterOptions = useMemo(() => {
    const opts: AssigneeOption[] = [{ id: UNASSIGNED, email: UNASSIGNED, name: 'Unassigned' }];
    for (const userId of allUserIds) {
      const user = assigneeUsersMap.get(userId);
      opts.push({
        id: userId,
        name: user?.name,
        email: user?.email || userId,
        available: user?.available ?? false,
      });
    }
    // Ensure the current user is always selectable (the default pre-selects them). They
    // may not be on any org team returned above; inject a plain entry only when missing.
    if (meValue && !opts.some(o => o.email === meValue)) {
      opts.push({
        id: currentUser?.id ?? meValue,
        email: meValue,
        name: currentUser?.name || meValue,
      });
    }
    return opts;
  }, [allUserIds, assigneeUsersMap, meValue, currentUser]);

  const isLoading = workflowsLoading || casesLoading;

  const handlePageChange = (newPage: number) => {
    if (newPage > currentPage && hasMore) {
      // Advanced (Atlas) queries return a keyset next_page_token; standard queries
      // cursor by the last record's id. start_after_id carries either to the backend.
      const next = nextPageToken ?? nextCursor ?? rawCases[rawCases.length - 1]?.id;
      if (next) setCursorStack(s => [...s, next]);
    } else if (newPage < currentPage) {
      setCursorStack(s => (s.length > 1 ? s.slice(0, -1) : s));
    }
  };

  // Inline reassignment for the Assignee column. This is a cross-program view, so
  // the mutation is scoped to each case's own org/program (looked up from the
  // row), not the store's selected program.
  const handleAssigneeSelect = useCallback(
    (assigneeEmail: string, caseId: string, teamId?: string) => {
      const item = rawCases.find(c => c.id === caseId);
      const organizationId = item?.organization_id || selectedOrganizationId || '';
      const programId = item?.program_id || '';
      if (!organizationId || !programId) return;
      updateAssignmentForCase.mutate({
        organizationId,
        programId,
        caseId,
        assigned: assigneeEmail,
        teamId,
      });
    },
    [rawCases, selectedOrganizationId, updateAssignmentForCase],
  );

  // Create-case needs a program + workflow; the chosen manufacturer (PQC
  // workflow) carries both. Full navigation — not client-side — so the store is
  // rebuilt from the target program's URL (matches useProgramSwitcher). A
  // client-side navigate would race CreateCaseRouter's workflow_id validation
  // against the previously selected program's cached workflows.
  const handleCreateCaseForManufacturer = (workflow: Workflow) => {
    if (!selectedOrganizationId || !workflow.program_id || !workflow.id) return;
    window.location.href = `${buildRoutePath(
      '/create-case',
      selectedOrganizationId,
      workflow.program_id,
    )}?workflow_id=${workflow.id}`;
  };

  // The download menu no longer exports directly — it opens the column-mode
  // dialog (Common columns vs All columns), remembering the picked format.
  const handleExport = async (format: ExportFormat) => {
    setExportDialogFormat(format);
  };

  // Export the currently-filtered cases (all pages) as CSV/XLSX. Filters mirror
  // the visible list minus pagination. 'common' sources columns from the
  // create-case (aepqc_preprocessing) input schema (one file); 'all' downloads
  // one file per manufacturer with that workflow's full schema columns.
  const handleExportConfirm = async (mode: OrgExportColumnsMode) => {
    const { limit: _limit, start_after_id: _startAfterId, ...filters } = request;
    void _limit;
    void _startAfterId;
    const ok = await downloadOrgCaseRecords(
      filters,
      workflowNameById,
      exportDialogFormat ?? 'csv',
      mode,
    );
    if (ok) setExportDialogFormat(null);
  };

  const tableConfig = useMemo(() => {
    const base = createCasesTableConfig(
      null, // no single selected workflow — cross-program view
      undefined,
      undefined,
      handleAssigneeSelect,
      item => {
        const orgId = item.organization_id || selectedOrganizationId || '';
        const progId = item.program_id || '';
        if (!orgId || !progId || !item.id) return '#';
        return buildRoutePath(`/cases/${item.id}`, orgId, progId);
      },
      undefined,
      usersMap,
      usersLoading,
      undefined, // include the Assignee column (editable inline reassign)
      undefined,
      {
        includeColumns: ['manufacturer'],
        context: tableContext,
        activeTabId: activeQuickFilter,
      },
    );
    return {
      ...base,
      emptyState: {
        title: 'No cases match your filters',
        description: 'Try a different status tab, manufacturer, assignee, date range, or Case ID.',
      },
    };
  }, [
    usersMap,
    usersLoading,
    selectedOrganizationId,
    tableContext,
    activeQuickFilter,
    handleAssigneeSelect,
  ]);

  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item>Cases</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  if (!selectedOrganizationId) {
    return (
      <>
        {breadcrumb}
        <EmptyState
          title="No organization selected"
          description="Select an organization to view cases."
          icon={XCircle}
          iconClassName="text-muted-foreground"
        />
      </>
    );
  }

  if (error || workflowsError) {
    return (
      <>
        {breadcrumb}
        <ErrorContainer
          errorMessage={
            (error as Error)?.message ||
            (workflowsError as Error)?.message ||
            'Failed to load cases'
          }
        />
      </>
    );
  }

  if (!workflowsLoading && pqcWorkflows.length === 0) {
    return (
      <>
        {breadcrumb}
        <EmptyState
          title="No PQC manufacturers available to you"
          description="You don't have access to any PQC workflows in this organization."
          icon={Factory}
          iconClassName="text-muted-foreground"
        />
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      <HeaderActionsPortal>
        <div className="flex items-center gap-3">
          <DownloadDropdown
            workflowType="pqc"
            onlyShowIcon
            disabled={rawCases.length === 0 || isLoading || isExporting}
            onDownload={handleExport}
          />
          <Button
            data-analytics-id="case:create"
            size="sm"
            onClick={() => setCreateDialogOpen(true)}
            disabled={workflowsLoading}
          >
            Create Case
          </Button>
        </div>
      </HeaderActionsPortal>
      <ManufacturerPickerDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        workflows={pqcWorkflows}
        programsById={programsById}
        onCreate={handleCreateCaseForManufacturer}
      />
      <ExportCasesDialog
        open={exportDialogFormat !== null}
        onOpenChange={isOpen => {
          if (!isOpen) setExportDialogFormat(null);
        }}
        format={exportDialogFormat ?? 'csv'}
        filtersSummary={exportFiltersSummary}
        isExporting={isExporting}
        onExport={handleExportConfirm}
      />
      <div className="flex flex-col h-full">
        {/* Main Content — mirrors CasesContainer's sticky layout: the
         * overflow-hidden scroll boundary keeps the filter bar + pagination
         * footer fixed while only the table body scrolls. */}
        <div className="flex-1 px-6 overflow-hidden">
          <div className="flex flex-col h-full min-h-0">
            <div className="flex items-center gap-3 mt-4 flex-wrap">
              <SearchInput
                searchOptions={SEARCH_OPTIONS}
                activeSearchType={activeSearchType}
                searchValues={{ [activeSearchType]: searchValue }}
                onSearchValueChange={(_type, value) => setSearchValue(value)}
                onSearchTypeChange={type => {
                  setActiveSearchType(type);
                  setSearchValue('');
                }}
                placeholder={type => (type === 'case_id' ? 'Enter exact Case ID' : 'Search…')}
              />
              <SearchableMultiSelect
                options={manufacturerOptions}
                selected={selectedWorkflowIds}
                onChange={setSelectedWorkflowIds}
                placeholder="All manufacturers"
                searchPlaceholder="Search manufacturers..."
                icon={Factory}
                className="min-w-[220px] max-w-[320px]"
                showClearAll
              />
              <AssigneeDropdown
                mode="multi"
                options={assigneeFilterOptions}
                selected={assignedFilter}
                onChange={setAssignedFilter}
                placeholder="Select assignee"
                className="min-w-[200px] max-w-[300px] px-3"
                triggerIcon={UserCircle}
                searchPlaceholder="Search assignees..."
              />
              {!hidesDatePicker && (
                <DateRangePicker
                  initialDateFrom={naiveStringToDate(dateFrom)}
                  initialDateTo={naiveStringToDate(dateTo)}
                  timezone={timeZone}
                  onUpdate={({ range }) => {
                    if (!range.from) return;
                    setDateFrom(dateToNaiveString(range.from));
                    setDateTo(dateToNaiveString(range.to ?? range.from));
                  }}
                  showCompare={false}
                />
              )}
            </div>

            <div className="mt-4">
              <QuickFilters
                activeFilter={activeQuickFilter}
                onFilterChange={handleQuickFilterChange}
                filtersConfig={PQC_FILTERS}
                counts={quickFilterCounts}
                countsLoading={countsLoading}
              />
            </div>

            {/* Cases Table — direct flex child (matches CasesContainer); grows +
             * scrolls internally via GenericTable's own flex-1 min-h-0. */}
            <GenericTable
              config={tableConfig}
              data={rawCases}
              loading={isLoading}
              error={null}
              currentPage={currentPage}
              hasNextPage={hasMore}
              onPageChange={handlePageChange}
              searchTerm={debouncedSearch}
              pageSize={pageSize}
              pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
              onPageSizeChange={setPageSize}
              // Atlas/keyset tabs sort by a fixed sort_field — disable + clear
              // column-header sort there; standard state tabs get full column sort.
              sortState={isAdvanced ? [] : sortState}
              onSortChange={isAdvanced ? undefined : setSortState}
              selectionMode={false}
            />
          </div>
        </div>
      </div>
    </>
  );
};
